import { supabase } from './supabaseClient';

export type LimitType = 'uploads' | 'aiCalls' | 'voiceMinutes' | 'cases' | 'docs' | 'storage';

interface PlanLimitsStructure {
  uploads: number;
  aiCalls: number;
  voiceMinutes: number;
  cases: number;
  docs: number;
  storageMb: number;
}

// Limitele de bază pe care le impunem global pentru a preveni abuzurile extreme, 
// dar ele vor fi suprascrise de limitele individuale din DB.
export const BETA_LIMITS: PlanLimitsStructure = {
  uploads: 9999,
  aiCalls: 30, // 30 AI calls pe zi per cont
  voiceMinutes: 500,
  cases: 9999,
  docs: 9999,
  storageMb: 500
};

export const RateLimitService = {
  
  // Verifică limitele curente pe server
  async getUsageStats(): Promise<{ used: number; max: number; isExceeded: boolean }> {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return { used: 0, max: BETA_LIMITS.aiCalls, isExceeded: false };

    const { data, error } = await supabase
      .from('profiles')
      .select('ai_calls_today, max_ai_calls')
      .eq('id', user.id)
      .single();

    if (error || !data) return { used: 0, max: BETA_LIMITS.aiCalls, isExceeded: false };

    return {
      used: data.ai_calls_today || 0,
      max: data.max_ai_calls || BETA_LIMITS.aiCalls,
      isExceeded: (data.ai_calls_today || 0) >= (data.max_ai_calls || BETA_LIMITS.aiCalls)
    };
  },

  // Verificare sincronă (folosită pentru a afișa mesaje în UI fără delay de rețea dacă e posibil)
  // Această funcție o facem asyncronă acum, fiind legată de backend.
  async checkDailyLimit(type: 'uploads' | 'aiCalls' | 'voiceMinutes'): Promise<boolean> {
    if (type !== 'aiCalls') return true; // Momentan limităm strict tokenii AI (bani reali)
    
    const stats = await this.getUsageStats();
    return !stats.isExceeded;
  },

  // Check pentru Storage (Documente/Fișiere) - se mapează pe storage, nu pe nr. de Docs.
  async checkStorageLimit(fileSizeBytes: number): Promise<boolean> {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return true;

    const { data, error } = await supabase
      .from('profiles')
      .select('total_storage_mb, max_storage_mb')
      .eq('id', user.id)
      .single();

    if (error || !data) return true;

    const fileSizeMb = fileSizeBytes / (1024 * 1024);
    return (data.total_storage_mb || 0) + fileSizeMb <= (data.max_storage_mb || BETA_LIMITS.storageMb);
  },

  // Verificare pentru limita de dosare per utilizator
  async checkTotalLimit(type: 'cases' | 'docs', currentCount: number): Promise<boolean> {
    if (type === 'docs') return true; // Limităm documentele prin storage
    
    // Check for cases via Supabase RPC
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return false;

    const { data, error } = await supabase.rpc('can_create_case', { user_id: user.id });
    if (error) {
      console.error("Error checking case limit:", error);
      // Fallback fallback
      return currentCount < BETA_LIMITS.cases;
    }
    
    return data === true;
  },

  // Funcție sync păstrată doar pentru backwards compatibility temporar
  checkTotalLimitSync(type: 'cases' | 'docs', currentCount: number): boolean {
    return true;
  },

  // Adaugă +1 la apelurile AI pe server (apelând funcția RPC din Postgres)
  async incrementUsage(type: 'uploads' | 'aiCalls' | 'voiceMinutes', amount: number = 1) {
    if (type !== 'aiCalls') return;

    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;

    // Apelăm funcția SQL creată anterior
    await supabase.rpc('increment_ai_calls', { user_id: user.id });
  },

  // Adaugă MB la stocare după upload
  async addStorageUsage(fileSizeBytes: number) {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;

    const fileSizeMb = fileSizeBytes / (1024 * 1024);
    
    // Preluăm stocarea curentă
    const { data } = await supabase.from('profiles').select('total_storage_mb').eq('id', user.id).single();
    const current = data?.total_storage_mb || 0;

    await supabase.from('profiles')
      .update({ total_storage_mb: current + fileSizeMb })
      .eq('id', user.id);
  },

  getLimitMessage(type: LimitType): { title: string, msg: string } {
    switch(type) {
      case 'cases': return { title: 'Limită Dosare', msg: `Nu există limită de număr de dosare.` };
      case 'docs': return { title: 'Limită Documente', msg: `Nu există limită de număr de documente.` };
      case 'aiCalls': return { title: 'Limită AI Zilnică (Securitate Beta)', msg: `Ai atins limita gratuită de asistență AI pe ziua de azi. Contoarele se resetează la miezul nopții.` };
      case 'storage': return { title: 'Spațiu Insuficient', msg: `Ai atins limita de stocare în Cloud a cabinetului (500MB). Nu mai poți încărca fișiere noi.` };
      case 'voiceMinutes': return { title: 'Limită Voce', msg: `Ai atins limita de minute pentru asistent vocal.` };
      case 'uploads': return { title: 'Limită Upload', msg: `Ai atins limita de încărcări pe zi.` };
      default: return { title: 'Limită Atinsă', msg: 'Ai atins o limită a platformei.' };
    }
  }
};
